import React, { useCallback, useEffect, useState } from "react";
import chatIcon from "../assets/message-chat-circle.png";
import assistantAvatar from "../assets/user.png";
import styles from "./styles.module.scss";

const LOADER_SRC = "https://help.foodzee.tech/loader.js";

const GREETING = "Hi! How can I help you?";
const BUBBLE_DELAY = 900; // page settles first, then the assistant shows up
const TYPING_DURATION = 1500; // how long the three dots run
const CHAR_INTERVAL = 38; // per character of the greeting

/** "idle" — nothing yet · "typing" — dots · "writing" — greeting typing out */
type Phase = "idle" | "typing" | "writing";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Support chat. The widget itself — the panel, its iframe, the desktop/mobile
 * layouts — belongs to loader.js; we never re-implement or re-wrap it. All we
 * replace is its launcher: its own button is hidden and ours forwards the click,
 * so the design matches the site while the chat keeps its own behaviour.
 */
const SupportWidget = () => {
  const [ready, setReady] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [typed, setTyped] = useState(0);

  useEffect(() => {
    if (!document.querySelector(`script[src="${LOADER_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = LOADER_SRC;
      script.defer = true;
      document.body.appendChild(script);
    }

    // Until the loader's button really exists there is nothing to open, so stay
    // hidden rather than show a launcher that does nothing (script blocked, offline).
    const sync = () => {
      setReady(!!document.querySelector(".sw-fab"));
      setPanelOpen(!!document.querySelector(".sw-panel.sw-open"));
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  /* Greeting choreography: appear → type → written. */
  useEffect(() => {
    if (!ready) return;

    if (prefersReducedMotion()) {
      setPhase("writing");
      setTyped(GREETING.length);
      return;
    }

    const timers = [
      window.setTimeout(() => setPhase("typing"), BUBBLE_DELAY),
      window.setTimeout(() => setPhase("writing"), BUBBLE_DELAY + TYPING_DURATION),
    ];

    return () => timers.forEach(window.clearTimeout);
  }, [ready]);

  /* One timeout per character, so it cancels cleanly on unmount mid-word. */
  useEffect(() => {
    if (phase !== "writing" || typed >= GREETING.length) return;

    const timer = window.setTimeout(() => setTyped(typed + 1), CHAR_INTERVAL);
    return () => window.clearTimeout(timer);
  }, [phase, typed]);

  const toggleChat = useCallback(() => {
    const fab = document.querySelector<HTMLButtonElement>(".sw-fab");
    fab?.click();
  }, []);

  if (!ready) return null;

  return (
    <div className={`${styles.widget} ${panelOpen ? styles.chatOpen : ""}`}>
      {phase !== "idle" && (
        <button
          type="button"
          className={styles.bubble}
          onClick={toggleChat}
          aria-label={GREETING}
        >
          <img src={assistantAvatar} alt="" className={styles.avatar} />

          <span className={styles.bubbleText} aria-hidden="true">
            {/* Holds the bubble at its final size from the first frame, so neither
                the dots nor the typing make the bubble jump around. */}
            <span className={styles.textGhost}>{GREETING}</span>

            {phase === "typing" ? (
              <span className={styles.typingDots}>
                <span />
                <span />
                <span />
              </span>
            ) : (
              <span className={styles.textTyped}>
                {GREETING.slice(0, typed)}
                {typed < GREETING.length && <i className={styles.caret} />}
              </span>
            )}
          </span>

          {/* Tail. The arc is the design's 4px apex rounding: its tangent points sit
              3.23px back along each edge, which is where the 28.49/33.51 come from. */}
          <svg
            className={styles.tail}
            width="62"
            height="25"
            viewBox="0 0 62 25"
            aria-hidden="true"
          >
            <path
              d="M0 0 H62 L33.51 22.97 A4 4 0 0 1 28.49 22.97 Z"
              fill="#fff"
            />
          </svg>
        </button>
      )}

      <button
        type="button"
        className={styles.launcher}
        onClick={toggleChat}
        aria-label={panelOpen ? "Close support chat" : "Open support chat"}
      >
        <img src={chatIcon} alt="" className={styles.launcherIcon} />
      </button>
    </div>
  );
};

export default SupportWidget;
